/**
===================================================
Module: frontend.src.components.AdminUsers.tsx
Description: Admin panel for user management
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { User, SystemSettings } from "../types";
import { apiService } from "../services/api";
import TopBar from "./TopBar";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/AdminUsers.css";

interface DeleteUserModalProps {
    user: User;
    onClose: () => void;
    onConfirm: (permanent: boolean) => void;
    t: any;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ user, onClose, onConfirm, t }) => {
    return (
        <div className="modal-overlay">
            <div className="admin-modal delete-modal">
                <div className="modal-header">
                    <h2>{t('deleteUser.title', { defaultValue: 'Delete User' })}</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="admin-form-content">
                    <p className="delete-warning">
                        {t('deleteUser.warning', { name: user.username || user.email, defaultValue: `Are you sure you want to permanently delete ${user.username || user.email}?` })}
                    </p>
                    <p className="delete-description">
                        {t('deleteUser.permanentDesc', { defaultValue: 'This will completely remove the user record, all associated data, and their identity. This action cannot be undone.' })}
                    </p>
                </div>
                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose}>
                        {t('common:cancel', { defaultValue: 'Cancel' })}
                    </button>
                    <button
                        className="btn-primary btn-danger"
                        onClick={() => onConfirm(true)}
                    >
                        {t('deleteUser.confirmPermanent', { defaultValue: 'Permanently Delete' })}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminUsers: React.FC = () => {
    const { t } = useTranslation('admin');
    const [users, setUsers] = useState<User[]>([]);
    const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [companyFilter, setCompanyFilter] = useState("");
    const [companyNames, setCompanyNames] = useState<string[]>([]);
    const [sortColumn, setSortColumn] = useState<string>('user');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalUsers, setTotalUsers] = useState(0);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const addNotification = (notification: Omit<Notification, 'id'>) => {
        const id = Date.now().toString();
        setNotifications(prev => [...prev, { ...notification, id }]);
        return id;
    };

    const removeNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    // For editing
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // For creating
    const [isCreating, setIsCreating] = useState(false);
    const [newUser, setNewUser] = useState({
        email: "",
        password: "", // Optional
        first_name: "",
        last_name: "",
        total_page_limit: undefined as number | undefined,
        max_file_size_mb: undefined as number | undefined,
        max_file_pages: undefined as number | undefined,
        account_end_date: "",
        document_retention_days: undefined as number | undefined,
        company_name: "",
        department: ""
    });

    const loadUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await apiService.getAdminUsers({
                page: currentPage,
                page_size: pageSize,
                search: debouncedSearch || undefined,
                company: companyFilter || undefined,
                sort: sortColumn,
                sort_dir: sortDirection,
            });
            setUsers(response.users || []);
            setTotalUsers(response.total || 0);
            setCompanyNames(response.company_names || []);
        } catch (err) {
            addNotification({ type: 'error', message: t('notifications.loadError') });
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearch, companyFilter, sortColumn, sortDirection]);

    const loadSettings = useCallback(async () => {
        try {
            const settingsResponse = await apiService.getSettings();
            setSystemSettings(settingsResponse);
        } catch (err) {
            // settings load error is non-critical
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Debounce search input
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 400);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [searchQuery]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [companyFilter, pageSize]);

    const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '.') {
            e.preventDefault();
        }
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;

        try {
            setIsSaving(true);
            const updates = {
                email: editingUser.email,
                is_superuser: editingUser.is_superuser,
                account_status: editingUser.account_status,
                account_end_date: editingUser.account_end_date,
                total_page_limit: editingUser.total_page_limit ?? null,
                max_file_size_mb: editingUser.max_file_size_mb ?? null,
                max_file_pages: editingUser.max_file_pages ?? null,
                document_retention_days: editingUser.document_retention_days ?? null,
                account_expiry_days: editingUser.account_expiry_days ?? null,
                company_name: editingUser.company_name,
                department: editingUser.department,
            };

            const result = await apiService.updateAdminUser(editingUser.id, updates);

            setEditingUser(null);

            // If email was changed, backend returns a flag
            if (result?.temporary_password) {
                addNotification({
                    type: 'success',
                    message: t('notifications.emailChangeSuccess', { email: result.new_email }),
                    duration: 8000
                });
            } else {
                addNotification({ type: 'success', message: t('notifications.updateSuccess'), duration: 3000 });
            }

            loadUsers();
        } catch (err) {
            addNotification({ type: 'error', message: "Failed to update user" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();

        // Explicit validation for mandatory fields with clear alerts
        const missing = [];
        if (!newUser.email) missing.push(t('email', { defaultValue: 'Email' }));
        if (!newUser.first_name) missing.push(t('firstName', { defaultValue: 'First Name' }));
        if (!newUser.last_name) missing.push(t('lastName', { defaultValue: 'Last Name' }));

        if (missing.length > 0) {
            addNotification({ type: 'error', message: `${t('notifications.missingFieldsPrefix')}${missing.join(', ')}` });
            return;
        }

        // Positive integer validation
        const toValidate = [
            { val: newUser.total_page_limit, label: t('editUser.totalPageLimit') },
            { val: newUser.max_file_size_mb, label: 'Max File Size' },
            { val: newUser.max_file_pages, label: 'Max File Pages' }
        ];

        for (const item of toValidate) {
            if (item.val !== undefined && item.val <= 0) {
                addNotification({ type: 'error', message: `${item.label}: ${t('noZeroError')}` });
                return;
            }
        }

        try {
            setIsSaving(true);
            await apiService.createAdminUser(newUser);
            setIsCreating(false);
            setNewUser({
                email: "",
                password: "",
                first_name: "",
                last_name: "",
                total_page_limit: undefined,
                max_file_size_mb: undefined,
                max_file_pages: undefined,
                account_end_date: "",
                document_retention_days: undefined,
                company_name: "",
                department: ""
            });
            addNotification({ type: 'success', message: "User created successfully", duration: 3000 });
            loadUsers();
        } catch (err: any) {
            addNotification({ type: 'error', message: err.message || "Failed to create user" });
        } finally {
            setIsSaving(false);
        }
    };

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const handleDeleteClick = (user: User) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async (permanent: boolean) => {
        if (!userToDelete) return;
        try {
            await apiService.deleteAdminUser(userToDelete.id, permanent);
            addNotification({ type: 'success', message: t('notifications.deleteSuccess', { defaultValue: 'User deleted successfully' }) });
            loadUsers();
        } catch (error: any) {
            console.error('Failed to delete user:', error);
            addNotification({ type: 'error', message: error.message || t('notifications.deleteError', { defaultValue: 'Failed to delete user' }) });
        } finally {
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    const getSortIndicator = (column: string) => {
        if (sortColumn !== column) return ' ↕';
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    };

    // Server-side pagination
    const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

    return (
        <div className="admin-container">
            <div className="admin-content">
                <TopBar />

                <div className="admin-header-section">
                    <div className="breadcrumb">
                        <span className="breadcrumb-link" onClick={() => window.location.href = '/projects'}>
                            {t('common:home', { defaultValue: 'Home' })}
                        </span>
                        <span className="breadcrumb-separator">/</span>
                        <span>{t('title')}</span>
                    </div>

                    <div className="admin-title-row">
                        <h1>{t('title')}</h1>
                        <div className="header-actions">
                            <button
                                className="admin-header-btn primary"
                                onClick={() => {
                                    setIsCreating(true);
                                    const now = new Date();
                                    const expiryDays = systemSettings?.account_expiry_days || 30;
                                    const expiryDate = new Date(now);
                                    expiryDate.setDate(now.getDate() + (expiryDays > 0 ? expiryDays : 0));

                                    const yyyy = expiryDate.getFullYear();
                                    const mm = String(expiryDate.getMonth() + 1).padStart(2, '0');
                                    const dd = String(expiryDate.getDate()).padStart(2, '0');
                                    const defaultEndDate = `${yyyy}-${mm}-${dd}`;

                                    setNewUser(prev => ({
                                        ...prev,
                                        total_page_limit: systemSettings?.total_page_limit,
                                        max_file_size_mb: systemSettings?.max_file_size_mb,
                                        max_file_pages: systemSettings?.max_file_pages,
                                        account_end_date: defaultEndDate,
                                        document_retention_days: systemSettings?.document_retention_days
                                    }));
                                }}
                            >
                                + {t('createUser', { defaultValue: 'Create User' })}
                            </button>
                            <button
                                className="admin-header-btn secondary"
                                onClick={() => window.location.href = '/admin/settings'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="3"></circle>
                                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                </svg>
                                {t('settingsTitle')}
                            </button>
                            <div className="filter-box">
                                <select
                                    value={companyFilter}
                                    onChange={(e) => setCompanyFilter(e.target.value)}
                                >
                                    <option value="">{t('filter.allCompanies', { defaultValue: 'All Companies' })}</option>
                                    {companyNames.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="search-box">
                                <input
                                    type="text"
                                    placeholder={t('searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="admin-main">
                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th className="sortable-th" onClick={() => handleSort('user')}>{t('table.user')}{getSortIndicator('user')}</th>
                                    <th className="sortable-th" onClick={() => handleSort('company')}>{t('table.company', { defaultValue: 'Company' })}{getSortIndicator('company')}</th>
                                    <th className="sortable-th" onClick={() => handleSort('status')}>{t('table.status')}{getSortIndicator('status')}</th>
                                    <th className="sortable-th" onClick={() => handleSort('initialLogin')}>{t('table.initialLogin', { defaultValue: 'Initial Login' })}{getSortIndicator('initialLogin')}</th>
                                    <th className="sortable-th" onClick={() => handleSort('totalPages')}>{t('table.totalPages')}{getSortIndicator('totalPages')}</th>
                                    <th className="sortable-th" onClick={() => handleSort('expiry')}>{t('table.expiry')}{getSortIndicator('expiry')}</th>
                                    <th className="sortable-th" onClick={() => handleSort('registered')}>{t('table.registered')}{getSortIndicator('registered')}</th>
                                    <th>{t('table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={9} className="loading-cell">{t('common:loading')}</td></tr>
                                ) : users.length === 0 ? (
                                    <tr><td colSpan={9} className="empty-cell">{t('common:noData')}</td></tr>
                                ) : (
                                    users.map(user => (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="user-cell">
                                                    <span className="user-name">{user.full_name || user.username}</span>
                                                    <div className="user-sub-details">
                                                        <span className="user-email">{user.email}</span>
                                                        {(user.company_name || user.department) && (
                                                            <span className="user-org">
                                                                {user.company_name}{user.company_name && user.department ? ' / ' : ''}{user.department}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {user.is_superuser && <span className="admin-badge">{t('table.admin')}</span>}
                                                </div>
                                            </td>
                                            <td>{user.company_name || '-'}</td>
                                            <td>
                                                <span className={`status-badge status-${user.account_status || 'active'}`}>
                                                    {user.account_status === 'active' ? t('statusEnabled') :
                                                        user.account_status === 'deactivated' ? t('statusDisabled') : t('statusExpired')}
                                                </span>
                                            </td>
                                            <td>
                                                {user.last_login ? (
                                                    <span className="status-badge status-active">{t('common:yes', { defaultValue: 'Yes' })}</span>
                                                ) : (
                                                    <span className="status-badge status-deactivated">{t('common:no', { defaultValue: 'No' })}</span>
                                                )}
                                            </td>
                                            <td>
                                                {(user.total_page_limit ?? systemSettings?.total_page_limit ?? 0).toLocaleString()}
                                            </td>
                                            <td>
                                                {user.account_end_date ?
                                                    new Date(user.account_end_date).toLocaleDateString() :
                                                    user.created_at && systemSettings?.account_expiry_days ?
                                                        ((() => {
                                                            const start = new Date(user.created_at);
                                                            const expiry = new Date(start);
                                                            expiry.setDate(start.getDate() + systemSettings.account_expiry_days);
                                                            return (
                                                                <>
                                                                    {expiry.toLocaleDateString()}
                                                                </>
                                                            );
                                                        })()) : '-'}
                                            </td>
                                            <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                            <td>
                                                <button className="edit-user-btn" onClick={() => setEditingUser(user)}>
                                                    {t('common:edit')}
                                                </button>
                                                <button
                                                    className="delete-user-btn"
                                                    onClick={() => handleDeleteClick(user)}
                                                >
                                                    {t('common:delete', { defaultValue: 'Delete' })}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="pagination-controls" style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '12px 16px', borderTop: '1px solid #e5e7eb', fontSize: '14px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{t('common:show', { defaultValue: 'Show' })}</span>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>{t('common:entries', { defaultValue: 'entries' })}</span>
                            <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                                ({totalUsers} {t('common:total', { defaultValue: 'total' })})
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                            >«</button>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                            >‹</button>
                            <span style={{ padding: '4px 12px' }}>
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                            >›</button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                            >»</button>
                        </div>
                    </div>
                </div>
            </div>

            <NotificationSystem notifications={notifications} onRemove={removeNotification} />

            {editingUser && (
                <div className="modal-overlay">
                    <div className="admin-modal">
                        <div className="modal-header">
                            <h2>{t('editUser.title')}</h2>
                            <button className="close-btn" onClick={() => setEditingUser(null)}>&times;</button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="admin-form">
                            <div className="admin-form-content">
                                <div className="form-section">
                                    <h3>{t('editUser.accountInfo', { defaultValue: "Account Info" })}</h3>
                                    <div className="form-group">
                                        <label>{t('email', { defaultValue: "Email" })}</label>
                                        <input
                                            type="email"
                                            value={editingUser.email || ""}
                                            onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3>{t('table.status')}</h3>
                                    <div className="form-group">
                                        <label>{t('editUser.accountStatus')}</label>
                                        <select
                                            value={editingUser.account_status || 'active'}
                                            onChange={(e) => setEditingUser({ ...editingUser, account_status: e.target.value as any })}
                                        >
                                            <option value="active">{t('statusEnabled')}</option>
                                            <option value="deactivated">{t('statusDisabled')}</option>
                                            <option value="expired">{t('statusExpired')}</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('editUser.expiryDate')}</label>
                                        <input
                                            type="date"
                                            value={editingUser.account_end_date ? editingUser.account_end_date.split('T')[0] : ""}
                                            onChange={(e) => setEditingUser({ ...editingUser, account_end_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('editUser.documentRetention', { defaultValue: "Document Retention (Days)" })}</label>
                                        <input
                                            type="number"
                                            value={editingUser.document_retention_days ?? ""}
                                            placeholder={systemSettings?.document_retention_days?.toString() || ""}
                                            onKeyDown={handleNumberKeyDown}
                                            onChange={(e) => setEditingUser({
                                                ...editingUser,
                                                document_retention_days: e.target.value ? parseInt(e.target.value) : undefined
                                            })}
                                        />
                                        <p className="help-text">{t('editUser.helpText')}</p>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3>{t('organization', { defaultValue: "Organization" })}</h3>
                                    <div className="form-group">
                                        <label>{t('companyName', { defaultValue: "Company Name" })}</label>
                                        <input
                                            type="text"
                                            value={editingUser.company_name || ""}
                                            onChange={(e) => setEditingUser({ ...editingUser, company_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('department', { defaultValue: "Department / Branch" })}</label>
                                        <input
                                            type="text"
                                            value={editingUser.department || ""}
                                            onChange={(e) => setEditingUser({ ...editingUser, department: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3>{t('editUser.quotas')}</h3>
                                    <div className="form-group">
                                        <label>{t('editUser.totalPageLimit', { defaultValue: "Total Page Limit (Lifetime)" })}</label>
                                        <input
                                            type="number"
                                            value={editingUser.total_page_limit ?? ""}
                                            placeholder={systemSettings?.total_page_limit?.toString() || ""}
                                            onKeyDown={handleNumberKeyDown}
                                            onChange={(e) => setEditingUser({
                                                ...editingUser,
                                                total_page_limit: e.target.value ? parseInt(e.target.value) : undefined
                                            })}
                                        />
                                        <p className="help-text">{t('editUser.helpText')}</p>
                                        <small className="quota-usage-text">
                                            {t('editUser.usageFormula', {
                                                remaining: Math.max(0, (editingUser.total_page_limit ?? systemSettings?.total_page_limit ?? Infinity) - (editingUser.usage?.total_pages_processed || 0)),
                                                used: editingUser.usage?.total_pages_processed || 0
                                            })}
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label>Max File Size (MB)</label>
                                        <input
                                            type="number"
                                            value={editingUser.max_file_size_mb ?? ""}
                                            placeholder={systemSettings?.max_file_size_mb?.toString() || ""}
                                            onKeyDown={handleNumberKeyDown}
                                            onChange={(e) => setEditingUser({
                                                ...editingUser,
                                                max_file_size_mb: e.target.value ? parseInt(e.target.value) : undefined
                                            })}
                                        />
                                        <p className="help-text">{t('editUser.helpText')}</p>
                                    </div>
                                    <div className="form-group">
                                        <label>Max File Pages</label>
                                        <input
                                            type="number"
                                            value={editingUser.max_file_pages ?? ""}
                                            placeholder={systemSettings?.max_file_pages?.toString() || ""}
                                            onKeyDown={handleNumberKeyDown}
                                            onChange={(e) => setEditingUser({
                                                ...editingUser,
                                                max_file_pages: e.target.value ? parseInt(e.target.value) : undefined
                                            })}
                                        />
                                        <p className="help-text">{t('editUser.helpText')}</p>
                                    </div>
                                </div>

                                <div className="form-section admin-danger-section">
                                    <h3>{t('editUser.adminAccess', { defaultValue: 'Admin Access' })}</h3>
                                    <div className="admin-toggle-area">
                                        <div className="admin-toggle-info">
                                            <strong>{t('editUser.isSuperuser')}</strong>
                                            <p className="admin-toggle-warning">
                                                {t('editUser.adminWarning', { defaultValue: 'Granting admin rights gives this user full access to manage all users, settings, and system configuration.' })}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className={`admin-toggle-btn ${editingUser.is_superuser ? 'active' : ''}`}
                                            onClick={() => {
                                                if (!editingUser.is_superuser) {
                                                    const confirmed = window.confirm(
                                                        t('editUser.adminConfirm', {
                                                            name: editingUser.full_name || editingUser.username,
                                                            defaultValue: `Are you sure you want to grant admin rights to ${editingUser.full_name || editingUser.username}? This will give them full system access.`
                                                        })
                                                    );
                                                    if (confirmed) {
                                                        setEditingUser({ ...editingUser, is_superuser: true });
                                                    }
                                                } else {
                                                    const confirmed = window.confirm(
                                                        t('editUser.revokeAdminConfirm', {
                                                            name: editingUser.full_name || editingUser.username,
                                                            defaultValue: `Are you sure you want to revoke admin rights from ${editingUser.full_name || editingUser.username}? They will lose access to all admin features.`
                                                        })
                                                    );
                                                    if (confirmed) {
                                                        setEditingUser({ ...editingUser, is_superuser: false });
                                                    }
                                                }
                                            }}
                                        >
                                            {editingUser.is_superuser
                                                ? t('editUser.revokeAdmin', { defaultValue: 'Revoke Admin' })
                                                : t('editUser.grantAdmin', { defaultValue: 'Grant Admin' })
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setEditingUser(null)}>
                                    {t('common:cancel')}
                                </button>
                                <button type="submit" className="btn-primary" disabled={isSaving}>
                                    {isSaving ? t('editUser.saving') : t('editUser.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isCreating && (
                <div className="modal-overlay">
                    <div className="admin-modal">
                        <div className="modal-header">
                            <h2>{t('createUser', { defaultValue: "Create User" })}</h2>
                            <button className="close-btn" onClick={() => setIsCreating(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleCreateUser} className="admin-form">
                            <div className="admin-form-content">
                                <div className="form-section">
                                    <h3>{t('details', { defaultValue: "Details" })}</h3>
                                    <div className="form-group">
                                        <label>{t('email', { defaultValue: "Email" })} *</label>
                                        <input
                                            type="email"
                                            required
                                            value={newUser.email}
                                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('password', { defaultValue: "Password" })} (Optional)</label>
                                        <input
                                            type="text"
                                            value={newUser.password}
                                            placeholder="Leave empty to auto-generate"
                                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-row-group">
                                            <label>{t('firstName', { defaultValue: "First Name" })} *</label>
                                            <input
                                                type="text"
                                                required
                                                value={newUser.first_name}
                                                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-row-group">
                                            <label>{t('lastName', { defaultValue: "Last Name" })} *</label>
                                            <input
                                                type="text"
                                                required
                                                value={newUser.last_name}
                                                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3>{t('organization', { defaultValue: "Organization" })}</h3>
                                    <div className="form-group">
                                        <label>{t('companyName', { defaultValue: "Company Name" })}</label>
                                        <input
                                            type="text"
                                            value={newUser.company_name}
                                            onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('department', { defaultValue: "Department / Branch" })}</label>
                                        <input
                                            type="text"
                                            value={newUser.department}
                                            onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3>{t('accountSettings', { defaultValue: "Account Settings" })}</h3>
                                    <div className="form-group">
                                        <label>{t('editUser.expiryDate')}</label>
                                        <input
                                            type="date"
                                            value={newUser.account_end_date}
                                            onChange={(e) => setNewUser({ ...newUser, account_end_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('editUser.documentRetention', { defaultValue: "Document Retention (Days)" })}</label>
                                        <input
                                            type="number"
                                            value={newUser.document_retention_days ?? ""}
                                            placeholder={systemSettings?.document_retention_days?.toString() || ""}
                                            onKeyDown={handleNumberKeyDown}
                                            onChange={(e) => setNewUser({
                                                ...newUser,
                                                document_retention_days: e.target.value ? parseInt(e.target.value) : undefined
                                            })}
                                        />
                                        <p className="help-text">{t('editUser.helpText')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setIsCreating(false)}>
                                    {t('common:cancel')}
                                </button>
                                <button type="submit" className="btn-primary" disabled={isSaving}>
                                    {isSaving ? t('common:creating', { defaultValue: "Creating..." }) : t('create', { defaultValue: "Create" })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && userToDelete && (
                <DeleteUserModal
                    user={userToDelete}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setUserToDelete(null);
                    }}
                    onConfirm={handleConfirmDelete}
                    t={t}
                />
            )}
        </div>
    );
};

export default AdminUsers;
